import { useAuth0 } from '@auth0/auth0-react';
import Box from '@mui/material/Box';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import PreSchoolService from 'Lib/API/PreSchool';
import MyButton from 'components/MyButton';
import { FormikWizard } from 'formik-wizard-form';
import { getDateFormatForDatePicket } from 'helper/dateFormat';
import { uploadFile } from 'pages/Auth/Presigned';
import React, { useEffect, useRef, useState } from 'react';
import { toastr } from 'react-redux-toastr';
import { useHistory, useParams } from 'react-router-dom';
import { getOrganizationInfoFromLocalStorage } from 'utils/functions';
import {
  classInformation,
  createForm,
} from '../../ValidationSchema';
import './class.scss';
import ClassInformation from './newClassSteps/ClassInformation/ClassInformation';
import CreateForm from './newClassSteps/CreateForm/CreateForm';
import Pricing from './newClassSteps/Pricing/Pricing';
import { PricingErrorContext } from './PricingErrorContext';

const PricingStep = (props) => <Pricing {...props} />;

const CreateNewClass = () => {
  const orgDetails = getOrganizationInfoFromLocalStorage();
  const [finished, setFinished] = useState(false);
  const { getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState(false);
  const [pricingErrors, setPricingErrors] = useState({});
  const [pricingTouched, setPricingTouched] = useState({});
  const [coverImage, setCoverImage] = useState("");
  const [croppedImage, setCroppedImage] = useState(null);
  const { push } = useHistory();
  const { id: classId } = useParams();
  const formikRef = useRef();

  const dateFormate = getDateFormatForDatePicket();


  const requiredFieldsIds = [
    "7306E1AD-745F-4BEE-829C-71BB380ED61B",
    "99267192-0200-403C-813D-8AF374FE25D0",
    "8A37D6EF-A9C1-4561-A16C-991E40512778",
  ]
  const requiredFields = [
    {
			id: "7306E1AD-745F-4BEE-829C-71BB380ED61B",
			element: "TextInput",
			text: "Text Input",
			required: true,
			canHaveAnswer: true,
			canHavePageBreakBefore: true,
			canHaveAlternateForm: true,
			canHaveDisplayHorizontal: true,
			canHaveOptionCorrect: true,
			canHaveOptionValue: true,
			canPopulateFromApi: true,
			field_name: "text_input_06108B97-D7AC-42A5-8167-ECB948331A10",
			label: "First Name",
		},
		{
			id: "99267192-0200-403C-813D-8AF374FE25D0",
			element: "TextInput",
			text: "Text Input",
			required: true,
			canHaveAnswer: true,
			canHavePageBreakBefore: true,
			canHaveAlternateForm: true,
			canHaveDisplayHorizontal: true,
			canHaveOptionCorrect: true,
			canHaveOptionValue: true,
			canPopulateFromApi: true,
			field_name: "text_input_2F6A1BAF-2E0C-46BB-AD2E-617C3364D143",
			label: "Last Name",
		},
		{
			id: "8A37D6EF-A9C1-4561-A16C-991E40512778",
			element: "DatePicker",
			text: "Date",
			required: true,
			readOnly: false,
			defaultToday: false,
			canHavePageBreakBefore: true,
			canHaveAlternateForm: true,
			canHaveDisplayHorizontal: true,
			canHaveOptionCorrect: true,
			canHaveOptionValue: true,
			canPopulateFromApi: true,
			dateFormat: dateFormate,
			timeFormat: "hh:mm aa",
			showTimeSelect: false,
			showTimeSelectOnly: false,
			showTimeInput: false,
			field_name: "date_picker_ADA9781E-794B-47F3-BB10-87CD39F5A342",
			label: "Birth Date",
		},
  ]

  useEffect(() => {
    if (classId) {
      const getClass = async () => {
        try {
          if (formikRef.current) {
            formikRef.current.setFieldValue('loading', true);
          }
          const token = await getAccessTokenSilently();
          const response = await PreSchoolService.getClassById(token, classId);
          const data = response?.result;
          const classInfo = {
            class_name: data?.class_name,
            year: data?.year,
            registration_starts: data?.class_start_date,
            registration_ends: data?.class_end_date,
            alwaysAvailable: data?.always_available,
            description: data?.description?.replace(/&lt;/g, '<'),
            allow_non_members_register: !orgDetails?.memberPortal ? true : data?.allow_non_members_register,
            allow_waiting_list: data?.allow_waiting_List,
            class_capacity: data?.class_details?.maximum_participants,
            infiniteParticipants: data?.class_details?.infiniteParticipants,
            age_range: `${data?.class_details?.ageStart}-${data?.class_details?.ageEnd}`,
            grade: data?.class_details?.grade ?? 'Grade 1',
            type: data?.type ?? 'Age',
            prices: data?.class_pricing?.prices?.map((item) => ({
              ...item,
              limitType: item?.limitType || 'amount',
              variantInfo: item?.variant ? transformVariantInfoToArray(item?.variantInfo) : [],
              type:
              item?.type === 'optional'
              ? 'Optional' :
                item?.type === 'onRegistration'
                  ? 'On Registration'
                  : item?.type === 'noPayment'
                  ? 'No Payment Needed'
                  : 'On Approval',
            })),
            discount: data?.class_discount,
            tags: data?.class_details?.tags || [],
            accountTags: data?.class_details?.accountTags || [],
            formId: data?.form,
            requiredFelids: required_form_field,
            step1Active: 'generalInfo',
            step2Active: 'costInfo',
            step3Active: 'Templates',
            background: data?.background,
            requiredFormFields: requiredFields,
            requiredFieldsIds: requiredFieldsIds,
          };
          setCroppedImage(data?.background);
          if (formikRef.current) {
            console.log('formikRef.current: ', formikRef.current);
            formikRef.current.setValues(classInfo);
          }
        } catch (error) {
          toastr.error('Error', 'Something went wrong');
        } finally {
          if (formikRef.current) {
            formikRef.current.setFieldValue('loading', false);
          }
        }
      };
      getClass();
    }
  }, [classId, getAccessTokenSilently]);

  const transformVariantInfoToObject = (variantInfo) => {
    const newVariantInfo = {};
    variantInfo.forEach((item) => {
      newVariantInfo[item?.name] = {
        ...(item?.infiniteQuantity ? { 
          infiniteQuantity: item?.infiniteQuantity, 
          amount: +item?.amount,
          forMemberOnly: item?.forMemberOnly,
        } : { 
          quantity: +item?.quantity, 
          amount: +item?.amount,
          forMemberOnly: item?.forMemberOnly,
        }),
      }
    })
    return newVariantInfo;
  }
  const transformVariantInfoToArray = (variantInfo) => {
    const newVariantInfo = [];
    if(!variantInfo || Object.keys(variantInfo).length === 0) return newVariantInfo;
    for (const key in variantInfo) {
      newVariantInfo.push({
        name: key,
        quantity: variantInfo[key]?.quantity || "",
        infiniteQuantity: variantInfo[key]?.infiniteQuantity || false,
        amount: variantInfo[key]?.amount || "",
        forMemberOnly: variantInfo[key]?.forMemberOnly,
      })
    }
    return newVariantInfo;
  }

  const handleCreateClass = async (values) => {
    const data = {
      class_name: values?.class_name,
      year: values?.year,
      class_start_date: values?.registration_starts,
      class_end_date: values?.alwaysAvailable
        ? null
        : values?.registration_ends,
      always_available: values?.alwaysAvailable,
      description: values?.description,
      allow_waiting_List: values?.allow_waiting_list,
      allow_non_members_register: values?.allow_non_members_register,
      class_details: {
        ageStart: values?.age_range?.split('-')[0],
        ageEnd: values?.age_range?.split('-')[1],
        gender: values?.gender,
        ...((values?.class_capacity && !values?.infiniteParticipants) ? {maximum_participants: values?.class_capacity} : {}),
        grade: values?.grade,
        infiniteParticipants: values?.infiniteParticipants,
        tags: values?.tags || [],
        accountTags: values?.accountTags || [],
      },
      class_pricing: {
        prices: values?.prices?.map((item) => ({
          ...item,
          amount: (item?.type === 'Optional' && item?.variant ) || (item?.type === 'No Payment Needed') ? undefined : item?.amount,
          type:
          item?.type === 'Optional' ? 'optional' : item?.type === 'On Registration'
              ? 'onRegistration'
              : item?.type === 'No Payment Needed'
              ? 'noPayment'
              : 'onApprove',
          ledger: item?.type === 'No Payment Needed' ? undefined : item?.ledger,
          subLedger:
            item?.type === 'No Payment Needed' ? undefined : item?.subLedger,
          tax_deductible: item?.tax_deductible
            ? +item?.tax_deductible
            : undefined,
          chargeDate:
            item?.type === 'On Approval' ? item?.chargeDate : undefined,
          dueDate: item?.type === 'On Approval' ? item?.dueDate : undefined,
          note: item?.note ? item?.note : undefined,
          split_duration: item?.is_splitted && item?.split_duration ? item?.split_duration : undefined,
          number_of_split: item?.is_splitted && item?.number_of_split ? item?.number_of_split : undefined,
          required_notes: undefined,
          variant: item?.type === "Optional" ? item?.variant : undefined,
          quantity: (item?.type === "Optional" && (!item?.infiniteQuantity && item?.limitType === 'amount') && !item?.variant)? item?.quantity : undefined,
          infiniteQuantity: (item?.type === "Optional" && !item?.variant) ? item?.infiniteQuantity : undefined, 
          limitType: item?.type === "Optional" ? item?.limitType ? item?.limitType : 'amount' : undefined,
          variantInfo: item?.type === "Optional" && item?.variantInfo?.length && item?.variant ? transformVariantInfoToObject(item?.variantInfo) : undefined,
        })),
      },
      class_discount: values?.discount?.map((item) => ({
        discount_name: item?.discount_name,
        discount_type:
          item?.discount_type === 'Date'
            ? 'date'
            : item?.discount_type === 'Amount'
            ? 'amount'
            : 'quantity',

        // price_type:
        //   values?.discount_value_type === 'fixed_price'
        //     ? 'fixed'
        //     : 'percentage',
        price_type: item?.price_type,
        value: item?.value,
        start_date:
          item?.discount_type === 'Date' ? item?.start_date : undefined,
        end_date: item?.discount_type === 'Date' ? item?.end_date : undefined,
        amount: item?.amount,
        quantity: item?.quantity,
        forMemberOnly: item?.forMemberOnly,
      })),
      form: values?.formId,
    };
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const newUrl = coverImage
      ? await uploadFile(coverImage, coverImage, token)
      : "";
      if (typeof newUrl.fileName === "string") {
        data.background = newUrl.fileName;
      }
      await PreSchoolService.createClass(token, data);
      toastr.success('Success', 'Class created successfully');
      push('/religious-schools');
    } catch (error) {
      toastr.error('Error', error?.data?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateClass = async (values) => {
    const data = {
      class_name: values?.class_name,
      year: values?.year.toString(),
      class_start_date: values?.registration_starts,
      class_end_date: values?.alwaysAvailable
        ? null
        : values?.registration_ends,
      always_available: values?.alwaysAvailable,
      description: values?.description,
      allow_waiting_List: values?.allow_waiting_list,
      allow_non_members_register: values?.allow_non_members_register,
      class_details: {
        ageStart: values?.age_range?.split('-')[0],
        ageEnd: values?.age_range?.split('-')[1],
        gender: values?.gender,
        ...((values?.class_capacity && !values?.infiniteParticipants) ? {maximum_participants: values?.class_capacity} : {}),
        grade: values?.grade,
        infiniteParticipants: values?.infiniteParticipants,
        tags: values?.tags || [],
        accountTags: values?.accountTags || [],
      },
      class_pricing: {
        prices: values?.prices?.map((item) => ({
          ...item,
          amount: (item?.type === 'Optional' && item?.variant ) || (item?.type === 'No Payment Needed') ? undefined : item?.amount,
          type:
            item?.type === 'Optional' ? 'optional' 
            : item?.type === 'On Registration'
              ? 'onRegistration'
              : item?.type === 'No Payment Needed'
              ? 'noPayment'
              : 'onApprove',
          ledger: item?.type === 'No Payment Needed' ? undefined : item?.ledger,
          subLedger:
            item?.type === 'No Payment Needed' ? undefined : item?.subLedger,
          tax_deductible: item?.tax_deductible
            ? +item?.tax_deductible
            : undefined,
          chargeDate:
            item?.type === 'On Approval' ? item?.chargeDate : undefined,
          dueDate: item?.type === 'On Approval' ? item?.dueDate : undefined,
          note: item?.note ? item?.note : undefined,
          _id: undefined,
          split_duration: item?.is_splitted && item?.split_duration ? item?.split_duration : undefined,
          number_of_split: item?.is_splitted && item?.number_of_split ? item?.number_of_split : undefined,
          required_notes: undefined,
          variant: item?.type === "Optional" ? item?.variant : undefined,
          quantity: (item?.type === "Optional" && (!item?.infiniteQuantity && item?.limitType === 'amount') && !item?.infiniteQuantity && !item?.variant)? item?.quantity : undefined,
          infiniteQuantity: (item?.type === "Optional" && !item?.variant) ? item?.infiniteQuantity : undefined,
          limitType: item?.type === "Optional" ? item?.limitType ? item?.limitType : 'amount' : undefined, 
          variantInfo: item?.type === "Optional" && item?.variantInfo?.length && item?.variant ? transformVariantInfoToObject(item?.variantInfo) : undefined,
        })),
      },
      class_discount: values?.discount?.map((item) => ({
        discount_name: item?.discount_name,
        discount_type:
          item?.discount_type === 'Date' || item?.discount_type === 'date'
            ? 'date'
            : item?.discount_type === 'Amount' ||
              item?.discount_type === 'amount'
            ? 'amount'
            : 'quantity',
        // price_type:
        //   values?.discount_value_type === 'fixed_price'
        //     ? 'fixed'
        //     : 'percentage',
        price_type: item?.price_type,
        value: item?.value,
        start_date:
          item?.discount_type === 'Date' || item?.discount_type === 'date'
            ? item?.start_date
            : undefined,
        end_date:
          item?.discount_type === 'Date' || item?.discount_type === 'date'
            ? item?.end_date
            : undefined,
        amount: item?.amount,
        quantity: item?.quantity,
        forMemberOnly: item?.forMemberOnly,
      })),
      form: values?.formId,
    };
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const newUrl = coverImage
      ? await uploadFile(coverImage, coverImage, token)
      : "";
      if (typeof newUrl.fileName === "string") {
        data.background = newUrl.fileName;
      }
      await PreSchoolService.updateClass(token, data, classId);
      toastr.success('Success', 'Class updated successfully');
      push('/religious-schools');
    } catch (error) {
      toastr.error('Error', error?.data?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };




  const handleNextStep = async (values, currentStepIndex, handleNext) => {
    if (currentStepIndex === 0) {
      const errors = await formikRef.current.validateForm();
      if (Object.keys(errors).length > 0) {
        const newTouched = { ...formikRef.current.touched };
        if (errors.class_name) newTouched.class_name = true;
        if (errors.year) newTouched.year = true;
        if (errors.registration_starts) newTouched.registration_starts = true;
        if (errors.registration_ends) newTouched.registration_ends = true;
        if (errors.class_capacity) newTouched.class_capacity = true;
        if (errors.age_range) newTouched.age_range = true;
        formikRef.current.setTouched(newTouched);
        const missing = [];
        if (errors.class_name) missing.push('Class Name');
        if (errors.year) missing.push('Year');
        if (errors.registration_starts) missing.push('Registration Starts');
        if (errors.registration_ends) missing.push('Registration Ends');
        if (errors.class_capacity) missing.push('Class Capacity');
        if (errors.age_range) missing.push('Age Range');
        toastr.info(
          'Validation',
          missing.length > 0
            ? `Please fill the following required fields: ${missing.join(', ')}`
            : 'Please fill all the mandatory fields'
        );
        return;
      }
    }
    if (currentStepIndex === 1) {
      if (!values?.prices.length) {
        toastr.info('Validation', 'Please add at least one price');
        return;
      }
      const missing = [];
      const newTouched = { ...formikRef.current.touched };
      const stepErrors = {};
      newTouched.prices = newTouched.prices ? [...newTouched.prices] : [];
      stepErrors.prices = [];
      for (const [i, price] of (values?.prices || []).entries()) {
        const label = price?.title || `Price ${i + 1}`;
        if (!newTouched.prices[i]) newTouched.prices[i] = {};
        if (!stepErrors.prices[i]) stepErrors.prices[i] = {};
        if (!price.type) {
          newTouched.prices[i].type = true;
          stepErrors.prices[i].type = 'Required';
          missing.push(`${label} - Type`);
        }
        if (price.type === 'On Approval') {
          if (!price.amount || price.amount === '') {
            newTouched.prices[i].amount = true;
            stepErrors.prices[i].amount = 'Required';
            missing.push(`${label} - Amount`);
          }
          if (!price.ledger || JSON.stringify(price.ledger) === '{}') {
            newTouched.prices[i].ledger = true;
            stepErrors.prices[i].ledger = 'Required';
            missing.push(`${label} - Ledger`);
          }
          if (!price.chargeDate) {
            newTouched.prices[i].chargeDate = true;
            stepErrors.prices[i].chargeDate = 'Required';
            missing.push(`${label} - Charge Date`);
          }
          if (!price.dueDate) {
            newTouched.prices[i].dueDate = true;
            stepErrors.prices[i].dueDate = 'Required';
            missing.push(`${label} - Due Date`);
          }
        } else if (price.type === 'On Registration') {
          if (!price.amount || price.amount === '') {
            newTouched.prices[i].amount = true;
            stepErrors.prices[i].amount = 'Required';
            missing.push(`${label} - Amount`);
          }
          if (!price.ledger || JSON.stringify(price.ledger) === '{}') {
            newTouched.prices[i].ledger = true;
            stepErrors.prices[i].ledger = 'Required';
            missing.push(`${label} - Ledger`);
          }
        } else if (price.type === 'Optional') {
          if (!price.variant && (!price.amount || price.amount === '')) {
            newTouched.prices[i].amount = true;
            stepErrors.prices[i].amount = 'Required';
            missing.push(`${label} - Amount`);
          }
          if (!price.ledger || JSON.stringify(price.ledger) === '{}') {
            newTouched.prices[i].ledger = true;
            stepErrors.prices[i].ledger = 'Required';
            missing.push(`${label} - Ledger`);
          }
          if (!price.variant && !price.infiniteQuantity && !price.quantity && price.limitType === 'amount') {
            newTouched.prices[i].quantity = true;
            stepErrors.prices[i].quantity = 'Required';
            missing.push(`${label} - Quantity`);
          }
          if (price.variant && !price.variantInfo?.length) {
            missing.push(`${label} - Please add at least one variant`);
          } else if (price.variant && price.variantInfo?.length) {
            const names = new Set();
            stepErrors.prices[i].variantInfo = [];
            newTouched.prices[i].variantInfo = newTouched.prices[i].variantInfo ? [...newTouched.prices[i].variantInfo] : [];
            for (const [vi, item] of price.variantInfo.entries()) {
              if (!newTouched.prices[i].variantInfo[vi]) newTouched.prices[i].variantInfo[vi] = {};
              if (!stepErrors.prices[i].variantInfo[vi]) stepErrors.prices[i].variantInfo[vi] = {};
              if (!item?.name) {
                newTouched.prices[i].variantInfo[vi].name = true;
                stepErrors.prices[i].variantInfo[vi].name = 'Required';
                missing.push(`${label} - Variant Name`);
              }
              if (!item?.infiniteQuantity && !item?.quantity) {
                newTouched.prices[i].variantInfo[vi].quantity = true;
                stepErrors.prices[i].variantInfo[vi].quantity = 'Required';
                missing.push(`${label} - Variant Quantity`);
              }
              if (!item?.amount) {
                newTouched.prices[i].variantInfo[vi].amount = true;
                stepErrors.prices[i].variantInfo[vi].amount = 'Required';
                missing.push(`${label} - Variant Amount`);
              }
              if (item?.name) {
                if (names.has(item.name.trim())) {
                  toastr.info('Validation', `${label} - Please enter unique variant name`);
                  return;
                }
                names.add(item.name.trim());
              }
            }
          }
        }
        if (price?.isInstallmentTransaction && !price?.installmentDetailsTransaction?.numberOfInstallment) {
          if (!newTouched.prices[i].installmentDetailsTransaction) newTouched.prices[i].installmentDetailsTransaction = {};
          newTouched.prices[i].installmentDetailsTransaction.numberOfInstallment = true;
          if (!stepErrors.prices[i].installmentDetailsTransaction) stepErrors.prices[i].installmentDetailsTransaction = {};
          stepErrors.prices[i].installmentDetailsTransaction.numberOfInstallment = 'Required';
          missing.push(`${label} - Number of installments`);
        }
      }
      newTouched.discount = newTouched.discount ? [...newTouched.discount] : [];
      stepErrors.discount = [];
      for (const [i, discount] of (values?.discount || []).entries()) {
        const label = `Discount ${i + 1}`;
        if (!newTouched.discount[i]) newTouched.discount[i] = {};
        if (!stepErrors.discount[i]) stepErrors.discount[i] = {};
        if (!discount?.discount_name) {
          newTouched.discount[i].discount_name = true;
          stepErrors.discount[i].discount_name = 'Required';
          missing.push(`${label} - Name`);
        }
        if (!discount?.value) {
          newTouched.discount[i].value = true;
          stepErrors.discount[i].value = 'Required';
          missing.push(`${label} - Value`);
        }
        if (discount.discount_type?.toLowerCase() === 'amount' && (!discount?.amount || discount?.amount < 1)) {
          newTouched.discount[i].amount = true;
          stepErrors.discount[i].amount = 'Required';
          missing.push(`${label} - Amount (must be >= 1)`);
        }
        if (discount.discount_type?.toLowerCase() === 'quantity' && (!discount?.quantity || discount?.quantity < 1)) {
          newTouched.discount[i].quantity = true;
          stepErrors.discount[i].quantity = 'Required';
          missing.push(`${label} - Quantity (must be >= 1)`);
        }
        if (discount.discount_type?.toLowerCase() === 'date') {
          if (!discount?.start_date) missing.push(`${label} - Start Date`);
          if (!discount?.end_date) missing.push(`${label} - End Date`);
        }
      }
      if (missing.length > 0) {
        setPricingErrors(stepErrors);
        setPricingTouched(newTouched);
        toastr.info('Validation', `Please fill the following required fields: ${missing.join(', ')}`);
        return;
      }
    }
    if (currentStepIndex === 2) {
      setFinished(true);
      if (classId) {
        handleUpdateClass(values);
      } else {
        handleCreateClass(values);
      }
    }
    handleNext();
  };

  const required_form_field = [
    {
      canHaveAlternateForm: true,
      canHaveAnswer: true,
      canHaveDisplayHorizontal: true,
      canHaveOptionCorrect: true,
      canHaveOptionValue: true,
      canHavePageBreakBefore: true,
      canPopulateFromApi: true,
      dirty: true,
      element: 'TextInput',
      field_name: 'text_input_06108B97-D7AC-42A5-8167-ECB948331A10',
      group_name: undefined,
      id: '7306E1AD-745F-4BEE-829C-71BB380ED61B',
      inline: undefined,
      label: 'First Name ',
      required: true,
      showDescription: undefined,
      static: undefined,
      text: 'Text Input',
    },
    {
      canHaveAlternateForm: true,
      canHaveAnswer: true,
      canHaveDisplayHorizontal: true,
      canHaveOptionCorrect: true,
      canHaveOptionValue: true,
      canHavePageBreakBefore: true,
      canPopulateFromApi: true,
      dirty: false,
      element: 'TextInput',
      field_name: 'text_input_2F6A1BAF-2E0C-46BB-AD2E-617C3364D143',
      group_name: undefined,
      id: '99267192-0200-403C-813D-8AF374FE25D0',
      inline: undefined,
      label: 'Last Name ',
      required: true,
      showDescription: undefined,
      static: undefined,
      text: 'Text Input',
    },
    {
      canHaveAlternateForm: true,
      canHaveDisplayHorizontal: true,
      canHaveOptionCorrect: true,
      canHaveOptionValue: true,
      canHavePageBreakBefore: true,
      canPopulateFromApi: true,
      dateFormat: dateFormate,
      defaultToday: false,
      element: 'DatePicker',
      field_name: 'date_picker_ADA9781E-794B-47F3-BB10-87CD39F5A342',
      group_name: undefined,
      id: '8A37D6EF-A9C1-4561-A16C-991E40512778',
      inline: undefined,
      label: 'Birth Date',
      required: true,
      showDescription: undefined,
      showTimeInput: false,
      showTimeSelect: false,
      showTimeSelectOnly: false,
      static: undefined,
      text: 'Date',
      timeFormat: 'hh:mm aa',
    },
  ];
  const initialValues = {
    class_name: '',
    year: '2023',
    registration_starts: '',
    registration_ends: '',
    alwaysAvailable: false,
    description: '',
    allow_non_members_register: true,
    infiniteParticipants: false,
    allow_waiting_list: false,
    class_capacity: '',
    type: 'Age',
    age_range: '',
    grade: 'Grade 1',
    prices: [],
    discount_value_type: 'fixed_price',
    discount_type: '',
    discount: [],
    internalDrawer: {},
    form_name: '',
    save_template_toggle: 'no',
    form: {},
    requiredFelids: required_form_field,
    formId: '',
    tags: [],
    accountTags: [],
    step1Active: 'generalInfo',
    step2Active: 'costInfo',
    step3Active: 'Templates',
    discountExpandedIndex: -1,
    background: "",
    requiredFormFields: requiredFields,
    requiredFieldsIds: requiredFieldsIds,
  };
  


  return (
    <PricingErrorContext.Provider value={{ pricingErrors, pricingTouched, setPricingErrors, setPricingTouched }}>
    <div className="class__container">
      <FormikWizard
        innerRef={formikRef}
        initialValues={initialValues}
        onSubmit={(values) => {}}
        steps={[
          {
            component: (props) => <ClassInformation coverImage={coverImage}
              setCoverImage={setCoverImage}
              setCroppedImage={setCroppedImage}
              croppedImage={croppedImage}
            {...props} />,
            validationSchema: classInformation,
          },
          {
            component: PricingStep,
          },
          {
            component: CreateForm,
          },
        ]}
      >
        {({
          values,
          errors,
          touched,
          isSubmitting,
          setFieldValue,
          currentStepIndex,
          renderComponent,
          handlePrev,
          handleNext,
          isNextDisabled,
          isPrevDisabled,
          handleSubmit,
          ...props
        }) => (
          <form onSubmit={handleSubmit}>
            <div className="top_section">
              <div className="header__title">{classId ? "Edit Class" : "New Class"}</div>
              <div className="class__steps">
                <Stepper activeStep={currentStepIndex}>
                  <Step completed={currentStepIndex > 0} className="step">
                    <StepLabel className="step-label">
                      Class Information
                    </StepLabel>
                  </Step>
                  <Step completed={currentStepIndex > 1}>
                    <StepLabel>Pricing</StepLabel>
                  </Step>
                  <Step completed={finished}>
                    <StepLabel>Create Form</StepLabel>
                  </Step>
                </Stepper>
              </div>
              <div className="action__section">
                <div className="action__buttons d-flex">
                  <MyButton
                    content={currentStepIndex === 0 ? 'Cancel' : 'Previous'}
                    onClick={() => {
                      if (currentStepIndex === 0) {
                        push('/religious-schools');
                      }
                      else {
                        handlePrev();
                      }
                    }}
                    style={{
                      marginRight: '1rem',
                      borderRadius: '0',
                      color: '#565656',
                      height: '39px',
                      width: '113px',
                      backgroundColor: '#fff',
                    }}
                  />
                  <MyButton
                    content={
                      currentStepIndex === 2 ? (
                        <span>
                          Save
                          {loading && (
                            <span className="ml-3 spinner spinner-gray"></span>
                          )}
                        </span>
                      ) : (
                        'Continue'
                      )
                    }
                    onClick={() =>
                      handleNextStep(values, currentStepIndex, handleNext)
                    }
                    disabled={loading}
                    style={{
                      width: '121px',
                      height: '36px',
                    }}
                  />
                </div>
              </div>
            </div>
            <Box>{renderComponent()}</Box>
          </form>
        )}
      </FormikWizard>
    </div>
    </PricingErrorContext.Provider>
  );
};

export default CreateNewClass;
